import React from "react";
import UboldSpinner from "../../ubold-components/UboldSpinner";
import {isFailed, isSuccessful, useImpellerDatasheet} from "../../business/useImpellerDatasheet";
import {DatasheetComponent} from "./DatasheetComponent";
import {Card} from "../../components/Card";
import {useFetchAllObjs} from "../../platform/hooks/PlatformTypeScriptHooks";
import {useMechanicalConfiguratorCriteriaControls} from "../../mechanicalconfig/useMechanicalConfiguratorCriteriaControls";
import {MechanicalConfigPreviewImage} from "../../mechanicalconfig/MechanicalConfigPreviewImage";
import {createInternationalizationPrefixer} from "../../platform/i18n/i18n";
import {SimulationRequestJson} from "../../business/ApiJsonTypes";
import {useQueryParamPredefinedCriteria} from "../SimulationPage";
import {ImpellerGeometryTable} from "../../components/assembly/ImpellerGeometryTable";
import {createAssemblyPrecisePreselectionCriteriaFromKnownDatasheeet, encodeAssemblyCriteriaToBase64} from "../mechanical/DatasheetToAssemblyPredefinedCriteria";
import {useNavigate} from "react-router-dom";
import {Protected} from "../../layout/Protected";
import {SelectionCriteriaDownloader} from "../selection/criteriaexport/SelectionCriteriaDownloader";
import {simulationRequestToSelectionRequest} from "../../business/simulationRequestToSelectionRequest";

const t = createInternationalizationPrefixer("FanSelectionCriteriaControls");

const tdatasheet = createInternationalizationPrefixer("InstalledFanDatasheet"); // reused SimulationDatasheet

export function DatasheetPage() {

    const queryParamPredefinedCriteriaAndSetter = useQueryParamPredefinedCriteria();

    const predefinedCriteria = queryParamPredefinedCriteriaAndSetter.predefinedCriteria! // Note ! (supposingly, sure to be present here, datasheet always comes with predefined criteria)

    const impellerDiameter = predefinedCriteria.fanFlowSpecification.fanDiameters.impellerDiameter;
    const adjustableImpellerId = predefinedCriteria.fanFlowSpecification.adjustableImpellerId;

    const availableShaftDiameters: number[] | null = useFetchAllObjs<number[]>("/api/mechanical/available-shaft-diameters/for-configuration?impellerDiameter=" + impellerDiameter + "&adjustableImpellerId=" + encodeURI(adjustableImpellerId));
    if (availableShaftDiameters == null) {
        return <div className={"text-center mt-5"}><UboldSpinner/></div>;
    }
    return <DatasheetPageLoaded availableShaftDiameters={availableShaftDiameters} predefinedCriteria={predefinedCriteria} updateQueryParamPredefinedCriteriaWithoutReload={queryParamPredefinedCriteriaAndSetter.updateQueryParamPredefinedCriteriaWithoutReload}/>;
}

export function DatasheetPageLoaded(props: { availableShaftDiameters: number[], predefinedCriteria: SimulationRequestJson, updateQueryParamPredefinedCriteriaWithoutReload: (newCriteria: SimulationRequestJson) => any }) {

    const navigate = useNavigate();

    const {
        currentlyConfiguredSimulationRequestJson,
        configuratorComponent,
        setLastUsedConfigurationSummary,
        wymiaryPrzylaczenioweZatwierdzone, // Simplified to 1 field since 18.36.0
        impellerHeightEditor,
        wymiaryPrzylaczenioweZatwierdzoneEditor,
    } = useMechanicalConfiguratorCriteriaControls(props.availableShaftDiameters, props.predefinedCriteria, props.updateQueryParamPredefinedCriteriaWithoutReload);

    const {
        datasheetResponse,
        loading
    } = useImpellerDatasheet(currentlyConfiguredSimulationRequestJson, setLastUsedConfigurationSummary);

    const getActiveH3Range = () => {
        if (!isSuccessful(datasheetResponse) || !datasheetResponse.h3RangeEstimationJson?.h3RangeEstimation) {
            return null;
        }

        const isSameSide = currentlyConfiguredSimulationRequestJson.mechanicalPreferences
            .preferredSupportAndClutchDiscSideCode === "SUPPORTS_AND_CLUTCH_SAME_SIDE";

        return isSameSide
            ? datasheetResponse.h3RangeEstimationJson.h3RangeEstimation.sameSideGuess
            : datasheetResponse.h3RangeEstimationJson.h3RangeEstimation.oppositeRangeGuess;
    };

    const h3Range = getActiveH3Range();
    const userSpecifiedH3 = impellerHeightEditor.enabled ? impellerHeightEditor.value : null;

    let h3ValidationAlert: { type: 'too-high' | 'too-low' | null } = { type: null };

    if (h3Range && userSpecifiedH3 !== null && userSpecifiedH3 !== undefined) {
        if (userSpecifiedH3 > h3Range.max) {
            h3ValidationAlert.type = 'too-high';
        } else if (userSpecifiedH3 < h3Range.min) {
            h3ValidationAlert.type = 'too-low';
        }
    }

    const housingDiameter = currentlyConfiguredSimulationRequestJson.fanFlowSpecification.fanDiameters.impellerDiameter / ((1.0 - 2.0 * (currentlyConfiguredSimulationRequestJson.fanFlowSpecification.fanDiameters.gap / 100.0)));

    const isSilentTypeFanSelected = isSuccessful(datasheetResponse) && (datasheetResponse.result.fanGeneralFanInformation.fanName.includes("(14") || datasheetResponse.result.fanGeneralFanInformation.fanName.includes("(15"));

    const miniGeometryTable = !loading && isSuccessful(datasheetResponse) && datasheetResponse?.result?.mechanicalResultDatasheetParameters != undefined &&
        <div className="d-flex justify-content-center flex-grow-1">
            <div className={"w-50"}>
                <ImpellerGeometryTable geometryReport={datasheetResponse.result.mechanicalResultDatasheetParameters.geometryReport}
                                       variant={"mini"}/>
            </div>
        </div>;


    const h3RangeComponent = <><h2 className={"pt-2 text-center"}>
        {isSuccessful(datasheetResponse) && datasheetResponse.h3RangeEstimationJson?.h3RangeEstimation && currentlyConfiguredSimulationRequestJson.mechanicalPreferences.preferredSupportAndClutchDiscSideCode == "SUPPORTS_AND_CLUTCH_SAME_SIDE" && <>
            H3 {datasheetResponse.h3RangeEstimationJson?.h3RangeEstimation?.sameSideGuess!.min} mm- {datasheetResponse.h3RangeEstimationJson?.h3RangeEstimation?.sameSideGuess!.max} mm
        </>}
        {isSuccessful(datasheetResponse) && datasheetResponse.h3RangeEstimationJson?.h3RangeEstimation && currentlyConfiguredSimulationRequestJson.mechanicalPreferences.preferredSupportAndClutchDiscSideCode == "SUPPORTS_AND_CLUTCH_OPPOSITE_SIDES" && <>
            H3 {datasheetResponse.h3RangeEstimationJson?.h3RangeEstimation?.oppositeRangeGuess!.min} mm - {datasheetResponse.h3RangeEstimationJson?.h3RangeEstimation?.oppositeRangeGuess!.max} mm
        </>}
        {isSuccessful(datasheetResponse) && !datasheetResponse.h3RangeEstimationJson && <div className={"text-center fs-5 text-gray-400 fw-light"}>{t("Estymata możliwych wysokości wirnika jest niedostępna")}</div>}
    </h2>
        <h3 className={"pt-2 text-secondary text-center"}>
            {isSuccessful(datasheetResponse) && datasheetResponse.h3RangeEstimationJson?.h3RangeEstimation && currentlyConfiguredSimulationRequestJson.mechanicalPreferences.preferredSupportAndClutchDiscSideCode == "SUPPORTS_AND_CLUTCH_SAME_SIDE" && <>
                H3 {datasheetResponse.h3RangeEstimationJson?.h3RangeEstimation?.oppositeRangeGuess!.min} mm- {datasheetResponse.h3RangeEstimationJson?.h3RangeEstimation?.oppositeRangeGuess!.max} mm
            </>}
            {isSuccessful(datasheetResponse) && datasheetResponse.h3RangeEstimationJson?.h3RangeEstimation && currentlyConfiguredSimulationRequestJson.mechanicalPreferences.preferredSupportAndClutchDiscSideCode == "SUPPORTS_AND_CLUTCH_OPPOSITE_SIDES" && <>
                H3 {datasheetResponse.h3RangeEstimationJson?.h3RangeEstimation?.sameSideGuess!.min} mm - {datasheetResponse.h3RangeEstimationJson?.h3RangeEstimation?.sameSideGuess!.max} mm
            </>}
        </h3></>;

    let optionalClutchlessHeightHintComponent;
    if (isSuccessful(datasheetResponse) && datasheetResponse.clutchlessHeightHint != null) {
        optionalClutchlessHeightHintComponent = <h3 className={"pt-2 text-secondary text-center"}>{datasheetResponse.clutchlessHeightHint.hintParameterName} = {datasheetResponse.clutchlessHeightHint.hintParameterValue} mm</h3>;
    } else {
        optionalClutchlessHeightHintComponent = <></>;
    }

    const mechanicalAssemblySucceeded = isSuccessful(datasheetResponse) && datasheetResponse.result.mechanicalResultDatasheetParameters != null;

    const approvedDimensionsBanner = <div className="row">
        <div className="col-12 mb-3">
            <div className={"alert alert-success d-flex align-items-center justify-content-between"}>
                <div>
                    <i className="fas fa-info-circle me-2"></i>
                    <strong>{t("Wymiary przyłączeniowe zatwierdzone")}</strong>
                </div>
                <button
                    className={"btn btn-sm btn-light-primary ms-3"}
                    onClick={() => wymiaryPrzylaczenioweZatwierdzoneEditor.populate("false")}
                >
                    {t("Cofnij zatwierdzenie wymiarów")}
                </button>
            </div>
        </div>
    </div>;

    const unapprovedDimensionsBanner = <div className="row">
        <div className="col-12 mb-3">
            <div className={"alert alert-warning d-flex align-items-center justify-content-between"}>
                <div>
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    <strong>{t("Wymiary mechaniczne niezatwierdzone")}</strong>
                </div>
                <button
                    className={"btn btn-sm btn-primary ms-3"}
                    onClick={() => wymiaryPrzylaczenioweZatwierdzoneEditor.populate("true")}
                >
                    {t("Zatwierdź wymiary przyłączeniowe")}
                </button>
            </div>
        </div>
    </div>;

    return <>
        {!wymiaryPrzylaczenioweZatwierdzone && <div className={"row"}>
            <div className={"col-12 mb-3"}>

                <Card header={tdatasheet("Konfigurator mechaniczny")}
                      toolbarElements={<SelectionCriteriaDownloader criteria={simulationRequestToSelectionRequest(currentlyConfiguredSimulationRequestJson)}/>}>
                    <div className="d-flex flex-row">
                        {configuratorComponent}
                        <div className={"flex-grow-1"}>

                            {currentlyConfiguredSimulationRequestJson.mechanicalPreferences.fanMountingOrientationCode === "VERTICAL" && <div className={"d-flex flex-row"}>
                                <MechanicalConfigPreviewImage
                                    orientantion={"vertical"}
                                    clutchOnInlet={currentlyConfiguredSimulationRequestJson.mechanicalPreferences.airflowRelativeClutchMountingSideCode === "CLUTCH_ON_INLET"}
                                    clutchPresent={currentlyConfiguredSimulationRequestJson.realizeWithClutch}
                                    silent={isSilentTypeFanSelected}
                                    sameSide={currentlyConfiguredSimulationRequestJson.mechanicalPreferences.preferredSupportAndClutchDiscSideCode === "SUPPORTS_AND_CLUTCH_SAME_SIDE"}/>
                                <div>
                                    <img className={"mw-100px"} src={isSilentTypeFanSelected ? "/img/dimensionpreview/x_y_silent_preview_vertical.png" : "/img/dimensionpreview/x_y_normal_preview_vertical.png"}/>
                                </div>
                                <div className={"flex-grow-1"}>
                                    {miniGeometryTable}
                                    {h3RangeComponent}
                                </div>
                            </div>}

                            {currentlyConfiguredSimulationRequestJson.mechanicalPreferences.fanMountingOrientationCode === "HORIZONTAL" && <>
                                <MechanicalConfigPreviewImage
                                    orientantion={"horizontal"}
                                    clutchOnInlet={currentlyConfiguredSimulationRequestJson.mechanicalPreferences.airflowRelativeClutchMountingSideCode === "CLUTCH_ON_INLET"}
                                    silent={isSilentTypeFanSelected}
                                    clutchPresent={currentlyConfiguredSimulationRequestJson.realizeWithClutch}
                                    sameSide={currentlyConfiguredSimulationRequestJson.mechanicalPreferences.preferredSupportAndClutchDiscSideCode === "SUPPORTS_AND_CLUTCH_SAME_SIDE"}/>
                                <div className={"d-flex"}>
                                    <div className={"w-120px"}>
                                        <img className={"mw-250px"} src={isSilentTypeFanSelected ? "/img/dimensionpreview/x_y_silent_preview.png" : "/img/dimensionpreview/x_y_normal_preview.png"}/>
                                    </div>
                                    <div className={"flex-grow-1"}>
                                        {miniGeometryTable}
                                        {h3RangeComponent}
                                        {optionalClutchlessHeightHintComponent}
                                    </div>
                                </div>
                            </>}


                        </div>

                    </div>
                </Card>
            </div>
        </div>}

        {!wymiaryPrzylaczenioweZatwierdzone && h3ValidationAlert.type === 'too-high' && (
            <div className="row">
                <div className="col-12 mb-3">
                    <div className={"alert alert-warning d-flex align-items-center"}>
                        <i className="fas fa-exclamation-triangle me-2"></i>
                        <span>
                            {t("Zmniejsz H3 - dla takiego wału czop sprzęgła jest za krótki")}
                            {" "}(H3 = {userSpecifiedH3?.toFixed(0)} mm, max = {h3Range?.max.toFixed(0)} mm)
                        </span>
                    </div>
                </div>
            </div>
        )}

        {!wymiaryPrzylaczenioweZatwierdzone && h3ValidationAlert.type === 'too-low' && (
            <div className="row">
                <div className="col-12 mb-3">
                    <div className={"alert alert-warning d-flex align-items-center"}>
                        <i className="fas fa-exclamation-triangle me-2"></i>
                        <span>
                            {t("Zwiększ H3 ponieważ czop napędu jest dłuższy niż sprzęgło")}
                            {" "}(H3 = {userSpecifiedH3?.toFixed(0)} mm, min = {h3Range?.min.toFixed(0)} mm)
                        </span>
                    </div>
                </div>
            </div>
        )}

        {wymiaryPrzylaczenioweZatwierdzone && approvedDimensionsBanner}
        {!wymiaryPrzylaczenioweZatwierdzone && mechanicalAssemblySucceeded && unapprovedDimensionsBanner}

        {loading && <Card>
            <div className={"p-5 text-center"}>
                <UboldSpinner/>
            </div>
        </Card>}
        {!loading && isSuccessful(datasheetResponse) && datasheetResponse.result &&
            <DatasheetComponent
                datasheet={datasheetResponse.result}
                memoizedDatasheetId={datasheetResponse.memoizedId}
                blueprintId={datasheetResponse.blueprintId ?? undefined}
                hideDimensionsApprovalBanner={true}
                paramsForInteractiveLocatedSplChart={{
                    housingDiameterMilimeter: housingDiameter,
                    operation: currentlyConfiguredSimulationRequestJson!.installationSpecification.operationalSetupCode as "INDUCED_DRAFT" | "FORCED_DRAFT",
                    pwlTot: parseFloat(datasheetResponse.result.workingPointPwlTot.value) // bit of a hack to reparse

                }}
            />}
        {!loading && isFailed(datasheetResponse) && <div className={"alert alert-warning p-4"}>There was an unexpected problem when generating datasheet.</div>}


        {!loading && isSuccessful(datasheetResponse) && datasheetResponse.result &&
            <Protected privilege={"PERM_FAN_ASSEMBLY"}>
                <button id={"go-to-assembly-button"}
                        className={"btn btn-primary"}
                        onClick={() => {
                            const assemblyCriteria = createAssemblyPrecisePreselectionCriteriaFromKnownDatasheeet(currentlyConfiguredSimulationRequestJson, datasheetResponse.result);
                            const encodedCriteria = encodeAssemblyCriteriaToBase64(assemblyCriteria);
                            navigate(`/impeller-assembly?predefined=${encodedCriteria}`);
                        }}>
                    Przejdź do złożenia wirnika
                </button>
            </Protected>}


    </>;


}
